import {
  addNode,
  createScene,
  makeRect,
  makeText,
  type Scene,
} from "../sceneGraph";
import type { NodeId } from "../sceneGraph";
import { easings } from "../animation";

/** Scene A — 200 cards with shadows, moderate motion. Stress fill rate. */
export function sceneA(w: number, h: number): Scene {
  const s = createScene();
  const cols = 20;
  const rows = 10;
  const cw = (w - 24) / cols;
  const ch = (h - 80) / rows;
  const palette: number[][] = [
    [1.00, 0.42, 0.21],
    [0.98, 0.74, 0.28],
    [0.31, 0.78, 0.47],
    [0.22, 0.55, 0.95],
    [0.62, 0.32, 0.93],
    [0.94, 0.30, 0.55],
  ];
  let z = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const pIdx = (r * cols + c) % palette.length;
      const pr = palette[pIdx][0];
      const pg = palette[pIdx][1];
      const pb = palette[pIdx][2];
      const cx = 12 + c * cw + cw * 0.5;
      const cy = 64 + r * ch + ch * 0.5;
      addNode(
        s,
        makeRect({
          x: cx - cw * 0.35,
          y: cy - ch * 0.35,
          w: cw * 0.7,
          h: ch * 0.7,
          radius: 8,
          fill: { r: pr, g: pg, b: pb, a: 1 },
          shadowOffsetX: 0,
          shadowOffsetY: 4,
          shadowBlur: 12,
          shadowSpread: 0,
          shadowColor: { r: 0, g: 0, b: 0, a: 0.35 },
          zIndex: z++,
        }),
      );
    }
  }
  return s;
}

/** Scene B — 500 small pills, no shadows. Stress instance throughput. */
export function sceneB(w: number, h: number): Scene {
  const s = createScene();
  const palette: number[][] = [
    [1.00, 0.42, 0.21],
    [0.98, 0.74, 0.28],
    [0.31, 0.78, 0.47],
    [0.22, 0.55, 0.95],
    [0.62, 0.32, 0.93],
    [0.94, 0.30, 0.55],
    [0.95, 0.95, 0.95],
  ];
  let z = 0;
  for (let i = 0; i < 500; i++) {
    const x = Math.random() * (w - 60);
    const y = 60 + Math.random() * (h - 100);
    const pIdx = i % palette.length;
    const pr = palette[pIdx][0];
    const pg = palette[pIdx][1];
    const pb = palette[pIdx][2];
    addNode(
      s,
      makeRect({
        x, y,
        w: 40 + Math.random() * 30,
        h: 14,
        radius: 7,
        fill: { r: pr, g: pg, b: pb, a: 0.85 + Math.random() * 0.15 },
        zIndex: z++,
      }),
    );
  }
  return s;
}

/** Scene C — fast horizontal motion. Stress velocity blur (Sprint 3) and
 *  frame-to-frame transform churn. */
export function sceneC(w: number, h: number): Scene {
  const s = createScene();
  const palette: number[][] = [
    [1.00, 0.42, 0.21],
    [0.22, 0.55, 0.55],
    [0.94, 0.30, 0.55],
  ];
  let z = 0;
  for (let i = 0; i < 60; i++) {
    const pIdx = i % palette.length;
    const pr = palette[pIdx][0];
    const pg = palette[pIdx][1];
    const pb = palette[pIdx][2];
    addNode(
      s,
      makeRect({
        x: 0,
        y: 80 + i * ((h - 120) / 60),
        w: 120,
        h: 14,
        radius: 7,
        fill: { r: pr, g: pg, b: pb, a: 1 },
        zIndex: z++,
        update: (n) => {
          // Linear motion across screen, wrapping. Constant per-step velocity so
          // the velocity field is well-defined frame-to-frame.
          n.x += (300 + (i % 4) * 50) / 60;
          if (n.x > w) n.x = -n.w;
        },
      }),
    );
  }
  return s;
}

/** Scene D — touch drag: 5 draggable cards. Laid out in a centered row using w. */
export function sceneD(w: number, h: number): Scene {
  const s = createScene();
  const colors: Array<{ r: number; g: number; b: number }> = [
    { r: 1.00, g: 0.42, b: 0.21 },
    { r: 0.22, g: 0.55, b: 0.95 },
    { r: 0.31, g: 0.78, b: 0.47 },
    { r: 0.62, g: 0.32, b: 0.93 },
    { r: 0.94, g: 0.30, b: 0.55 },
  ];
  const cardW = Math.min(160, Math.max(96, (w - 80) / colors.length - 12));
  const cardH = Math.min(120, h * 0.18);
  const totalW = colors.length * cardW + (colors.length - 1) * 16;
  const startX = (w - totalW) / 2;
  const startY = Math.max(80, h * 0.18);
  let z = 0;
  for (let i = 0; i < colors.length; i++) {
    const c = colors[i];
    addNode(
      s,
      makeRect({
        x: startX + i * (cardW + 16),
        y: startY + i * 8,
        w: cardW,
        h: cardH,
        radius: 16,
        fill: { r: c.r, g: c.g, b: c.b, a: 1 },
        strokeWidth: 2,
        stroke: { r: 1, g: 1, b: 1, a: 0.25 },
        shadowOffsetX: 0,
        shadowOffsetY: 6,
        shadowBlur: 16,
        shadowSpread: 0,
        shadowColor: { r: 0, g: 0, b: 0, a: 0.4 },
        zIndex: 10 + z++,
      }),
    );
  }
  return s;
}

/**
 * Scene E — animated motion-graphics demo. Two boxes travelling across the
 * screen at different speeds using keyframes: a "slow" card on top (so blur
 * barely shows) and a "fast" card on bottom (so blur is clearly visible).
 * Proves the keyframe/easing pipeline + velocity-based motion blur.
 */
export function sceneE(w: number, h: number): Scene {
  const s = createScene();
  const cardW = 100;
  const cardH = 60;
  const ySlow = h * 0.3 - cardH * 0.5;
  const yFast = h * 0.7 - cardH * 0.5;

  // Slow card: 4s ease-in-out, travels 60% of width
  addNode(
    s,
    makeRect({
      x: 0,
      y: ySlow,
      w: cardW,
      h: cardH,
      radius: 18,
      fill: { r: 0.62, g: 0.32, b: 0.93, a: 1 },
      shadowOffsetX: 0,
      shadowOffsetY: 8,
      shadowBlur: 24,
      shadowSpread: 0,
      shadowColor: { r: 0, g: 0, b: 0, a: 0.35 },
      zIndex: 100,
      update: (n) => {
        // Local clock for this node, looping every 4s
        const clock = (performance.now() / 1000) % 4;
        const leftX = 20;
        const rightX = w - cardW - 20;
        const t = clock / 4; // 0..1
        // Use easeInOutCubic
        const k = easings.easeInOutCubic(t);
        n.x = leftX + (rightX - leftX) * k;
      },
    }),
  );

  // Fast card: 1.2s linear-ish, full-screen sweep
  addNode(
    s,
    makeRect({
      x: 0,
      y: yFast,
      w: cardW,
      h: cardH,
      radius: 18,
      fill: { r: 1.00, g: 0.42, b: 0.21, a: 1 },
      shadowOffsetX: 0,
      shadowOffsetY: 8,
      shadowBlur: 24,
      shadowSpread: 0,
      shadowColor: { r: 0, g: 0, b: 0, a: 0.35 },
      zIndex: 101,
      update: (n) => {
        const clock = (performance.now() / 1000) % 1.2;
        const leftX = -cardW;
        const rightX = w;
        const t = clock / 1.2;
        // Sharp ease-out for visible speed at the start (where blur shines)
        const k = easings.easeOutCubic(t);
        n.x = leftX + (rightX - leftX) * k;
      },
    }),
  );

  return s;
}

/**
 * Scene F — keyframe animation demo. A single card bounces between positions
 * with explicit keyframes (proves animation.ts integration). Loops every 3s.
 */
export function sceneF(_w: number, h: number): Scene {
  const s = createScene();
  addNode(
    s,
    makeRect({
      x: 0,
      y: h * 0.5 - 40,
      w: 80,
      h: 80,
      radius: 24,
      fill: { r: 0.31, g: 0.78, b: 0.47, a: 1 },
      shadowOffsetX: 0,
      shadowOffsetY: 8,
      shadowBlur: 20,
      shadowColor: { r: 0, g: 0, b: 0, a: 0.4 },
      zIndex: 100,
      update: (n) => {
        const clock = performance.now() / 1000;
        // Make a Y bounce with keyframes. We need to redraw same number
        // of frames so import-level compile is fine — we mutate x,y live.
        const phase = clock % 3;
        // 0..1: drop (easeIn), 1..2: rise (easeOut)
        let t: number;
        if (phase < 1) {
          t = easings.easeInOutCubic(phase);
          n.y = h * 0.5 - 40 + 200 * t; // drop
        } else {
          t = easings.easeOutCubic((phase - 1) / 2);
          n.y = h * 0.5 - 40 + 200 * (1 - t); // rise
        }
      },
    }),
  );
  return s;
}

/**
 * Scene G — Layer 2 demo. Text + an image inside SDF cards, with the same
 * velocity blur, inertia, and rounded-rect shadows as the rect-only scenes.
 *
 * Shows that the rendering kernel handles ARBITRARY UI content, not just
 * colored boxes. The "proof of concept" Rus asked for: real shapes with
 * text in them, looking like a web page, running the same blur/drag code.
 */
export function sceneG(w: number, h: number): Scene {
  const s = createScene();
  const pad = 16;

  // Big card with title + subtitle
  addNode(s, makeRect({
    x: pad, y: 60,
    w: w - pad * 2, h: 180,
    radius: 18,
    fill: { r: 0.12, g: 0.12, b: 0.16, a: 1 },
    shadowOffsetX: 0, shadowOffsetY: 8, shadowBlur: 24, shadowSpread: 0,
    shadowColor: { r: 0, g: 0, b: 0, a: 0.4 },
    zIndex: 100,
  }));
  addNode(s, makeText({
    x: pad + 18, y: 60 + 22,
    w: w - pad * 2 - 36, h: 44,
    text: "NSPP",
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    fill: { r: 1, g: 1, b: 1, a: 1 },
    fontSize: 32,
    fontWeight: 700,
    zIndex: 110,
  }));
  addNode(s, makeText({
    x: pad + 18, y: 60 + 78,
    w: w - pad * 2 - 36, h: 80,
    text: "GPU SDF UI on iPhone — 60 FPS with video-like motion blur.",
    textColor: { r: 0.7, g: 0.78, b: 1.0, a: 1 },
    fill: { r: 0.7, g: 0.78, b: 1.0, a: 1 },
    fontSize: 16,
    fontWeight: 400,
    zIndex: 110,
  }));

  // Feature pill (a colored rect with text — like a tag)
  addNode(s, makeRect({
    x: pad + 18, y: 180 + 60 + 18,
    w: 84, h: 28,
    radius: 14,
    fill: { r: 1.0, g: 0.42, b: 0.21, a: 1 },
    zIndex: 110,
  }));
  addNode(s, makeText({
    x: pad + 18 + 12, y: 180 + 60 + 18 + 5,
    w: 84 - 24, h: 18,
    text: "active",
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    fill: { r: 1, g: 1, b: 1, a: 1 },
    fontSize: 12,
    fontWeight: 700,
    zIndex: 120,
  }));

  // 3 stat cards, each showing a number + label
  const statY = 300;
  const cardW = (w - pad * 2 - 16) / 3;
  const stats = [
    { label: "SDF", value: "100%", fill: { r: 0.22, g: 0.55, b: 0.95, a: 1 } },
    { label: "60", value: "FPS", fill: { r: 1.0, g: 0.42, b: 0.21, a: 1 } },
    { label: "28", value: "KB", fill: { r: 0.31, g: 0.78, b: 0.47, a: 1 } },
  ];
  for (let i = 0; i < stats.length; i++) {
    const x = pad + i * (cardW + 8);
    addNode(s, makeRect({
      x, y: statY, w: cardW, h: 90,
      radius: 14,
      fill: { r: 0.16, g: 0.16, b: 0.20, a: 1 },
      shadowOffsetX: 0, shadowOffsetY: 4, shadowBlur: 12, shadowSpread: 0,
      shadowColor: { r: 0, g: 0, b: 0, a: 0.35 },
      zIndex: 100 + i,
    }));
    addNode(s, makeText({
      x: x + 14, y: statY + 14,
      w: cardW - 28, h: 36,
      text: stats[i]!.value,
      textColor: { r: stats[i]!.fill.r, g: stats[i]!.fill.g, b: stats[i]!.fill.b, a: 1 },
      fill: { r: stats[i]!.fill.r, g: stats[i]!.fill.g, b: stats[i]!.fill.b, a: 1 },
      fontSize: 28,
      fontWeight: 800,
      zIndex: 110 + i,
    }));
    addNode(s, makeText({
      x: x + 14, y: statY + 56,
      w: cardW - 28, h: 20,
      text: stats[i]!.label,
      textColor: { r: 0.65, g: 0.68, b: 0.74, a: 1 },
      fill: { r: 0.65, g: 0.68, b: 0.74, a: 1 },
      fontSize: 12,
      fontWeight: 500,
      zIndex: 110 + i,
    }));
  }

  // Bottom bar with two buttons
  const btnY = h - 110;
  addNode(s, makeRect({
    x: pad, y: btnY, w: (w - pad * 2 - 12) / 2, h: 52,
    radius: 26,
    fill: { r: 1.00, g: 0.42, b: 0.21, a: 1 },
    shadowOffsetX: 0, shadowOffsetY: 4, shadowBlur: 14, shadowSpread: 0,
    shadowColor: { r: 1, g: 0.42, b: 0.21, a: 0.5 },
    zIndex: 100,
  }));
  addNode(s, makeText({
    x: pad + 14, y: btnY + 14,
    w: (w - pad * 2 - 12) / 2 - 28, h: 24,
    text: "Try drag",
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    fill: { r: 1, g: 1, b: 1, a: 1 },
    fontSize: 16,
    fontWeight: 700,
    zIndex: 110,
  }));

  addNode(s, makeRect({
    x: pad + (w - pad * 2 - 12) / 2 + 12, y: btnY,
    w: (w - pad * 2 - 12) / 2, h: 52,
    radius: 26,
    fill: { r: 0.16, g: 0.16, b: 0.20, a: 1 },
    strokeWidth: 1,
    stroke: { r: 0.45, g: 0.45, b: 0.5, a: 1 },
    zIndex: 100,
  }));
  addNode(s, makeText({
    x: pad + (w - pad * 2 - 12) / 2 + 12 + 14, y: btnY + 14,
    w: (w - pad * 2 - 12) / 2 - 28, h: 24,
    text: "Scene E",
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    fill: { r: 1, g: 1, b: 1, a: 1 },
    fontSize: 16,
    fontWeight: 700,
    zIndex: 110,
  }));

  return s;
}

/**
 * Scene Studio — interactive playground with selection, resize, persistence,
 * and selective background. The selection / resize / persistence logic lives
 * in src/studio.ts; this scene just provides 8 cards of varied sizes.
 */
export function sceneStudio(w: number, h: number): Scene {
  const s = createScene();
  const cards: Array<{ w: number; h: number; color: [number, number, number] }> = [
    { w: 80,  h: 80,  color: [1.00, 0.42, 0.21] },
    { w: 60,  h: 60,  color: [0.98, 0.74, 0.28] },
    { w: 140, h: 90,  color: [0.31, 0.78, 0.47] },
    { w: 100, h: 100, color: [0.22, 0.69, 0.95] },
    { w: 70,  h: 110, color: [0.62, 0.32, 0.93] },
    { w: 180, h: 60,  color: [0.94, 0.30, 0.55] },
    { w: 90,  h: 140, color: [0.30, 0.95, 0.85] },
    { w: 120, h: 120, color: [1.00, 0.55, 0.85] },
  ];

  const cy = h * 0.42;
  const startX = w * 0.08;
  const span = w * 0.84;
  let z = 0;
  for (let i = 0; i < cards.length; i++) {
    const c = cards[i];
    const cx = startX + (i / cards.length) * span;
    addNode(
      s,
      makeRect({
        x: cx - c.w * 0.5,
        y: cy + (i % 2 === 0 ? -40 : 40) - c.h * 0.5,
        w: c.w,
        h: c.h,
        radius: 12,
        fill: { r: c.color[0], g: c.color[1], b: c.color[2], a: 0.92 },
        strokeWidth: 2,
        stroke: { r: 1, g: 1, b: 1, a: 0.18 },
        shadowOffsetX: 0,
        shadowOffsetY: 4,
        shadowBlur: 14,
        shadowColor: { r: 0, g: 0, b: 0, a: 0.35 },
        zIndex: 10 + z++,
      }),
    );
  }
  return s;
}

/** Scene L6 — Mix-blend-mode verification. Three columns of color backdrops
 *  with CMY overlay rects: multiply a complementary color so the result is
 *  visibly different from the backdrop, and a screen overlay of the same color
 *  so the result visibly lightens. Plus a normal control rect so we can see
 *  the unblended comparison. Used for Layer 6 visual QA.
 */
/**
 * Scene L7 — 500-pill showpiece using every layer.
 *
 * Layer 1: SDF (rounded rect pills, motion blur from velocity FBO).
 * Layer 2: CSS-style row layout via computed positions.
 * Layer 3: Each pill carries a linear gradient (start → end color).
 * Layer 5: backdrop-filter blur on a glassy "stage" plate behind the pills.
 * Layer 6: mix-blend-mode "screen" so overlapping pills brighten additively.
 * Layer 7: per-node filter chain — each pill carries a different hue-rotate
 *          so the whole field cycles through the rainbow as pills animate.
 *
 * Animation: Lissajous-style drift per pill (different freq/phase per index)
 * produces flowing motion. Hits of the blur slider show the velocity trail.
 */
export function sceneL7(w: number, h: number): Scene {
  const s = createScene();

  // Backdrop-blurred stage plate — sits behind the pills with backdrop-filter
  // turned on so the pills' motion blur shows through the plate.
  addNode(
    s,
    makeRect({
      x: 40,
      y: 40,
      w: w - 80,
      h: h - 80,
      radius: 28,
      fill: { r: 0.08, g: 0.08, b: 0.12, a: 0.32 },
      shadowOffsetX: 0,
      shadowOffsetY: 0,
      shadowBlur: 0,
      filter: {
        blur: 0,
        brightness: 1,
        contrast: 1,
        saturate: 1.4,
        hueRotate: 0,
        invert: 0,
        backdropBlur: 24,
      },
      zIndex: 0,
    }),
  );

  // Header band — sits on top of the stage plate, slightly outside the pills.
  addNode(
    s,
    makeRect({
      x: 64,
      y: 56,
      w: w - 128,
      h: 2,
      fill: { r: 1, g: 1, b: 1, a: 0.18 },
      zIndex: 1,
    }),
  );

  // 500 pills — multi-row "rainbow field".
  const palette: Array<[number, number, number]> = [
    [1.00, 0.42, 0.21],   // orange
    [0.98, 0.74, 0.28],   // amber
    [0.31, 0.78, 0.47],   // green
    [0.22, 0.69, 0.95],   // sky
    [0.62, 0.32, 0.93],   // violet
    [0.94, 0.30, 0.55],   // magenta
  ];

  const cols = 50;
  const rows = 10;
  const cellW = (w - 80) / cols;
  const cellH = (h - 200) / rows;
  const pillW = cellW * 0.78;
  const pillH = 16;

  let z = 2;
  for (let i = 0; i < 500; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols) % rows;
    const cycle = Math.floor(i / (cols * rows));
    const pIdx = (i + cycle) % palette.length;
    const [pr, pg, pb] = palette[pIdx];

    // Per-pill end color for the gradient — saturated mid-tone (not white) so
    // the gradient stays vivid against the dark background.
    const er = Math.min(1, pr * 1.2 + 0.15);
    const eg = Math.min(1, pg * 1.2 + 0.15);
    const eb = Math.min(1, pb * 1.2 + 0.15);
    // Random-ish per-pill phase / freq so they don't all move in lockstep.
    const seed = i * 9301 + 49297;
    const r1 = ((seed * 233280) % 233280) / 233280;
    const r2 = ((seed * 1234567) % 233280) / 233280;
    const freqX = 0.3 + r1 * 0.6;
    const freqY = 0.2 + r2 * 0.5;
    const phaseX = r1 * Math.PI * 2;
    const phaseY = r2 * Math.PI * 2;

    // Each pill starts at its grid cell but the animation will drift it.
    const baseX = 40 + cellW * (col + 0.5) - pillW * 0.5;
    const baseY = 100 + cellH * (row + 0.5) - pillH * 0.5;
    const ampX = cellW * 0.18;
    const ampY = cellH * 0.22;

    // Per-node hue-rotate — drifts over time so the rainbow cycles smoothly.
    const hueBase = (i / 500) * Math.PI * 2;

    addNode(
      s,
      makeRect({
        x: baseX,
        y: baseY,
        w: pillW,
        h: pillH,
        radius: 8,
        fill: { r: pr, g: pg, b: pb, a: 0.92 },
        gradientEnd: { r: er, g: eg, b: eb, a: 1 },
        gradientAngle: Math.PI * (0.25 + (i % 5) * 0.1),
        mixBlendMode: i % 4 === 0 ? "screen" : "normal",
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        shadowBlur: 0,
        filter: {
          blur: 0,
          brightness: 1,
          contrast: 1,
          saturate: 1,
          hueRotate: hueBase,
          invert: 0,
        },
        zIndex: z++,
        update: (n, t) => {
          // Lissajous drift — small loop in CSS px space.
          n.x = baseX + Math.sin(t * freqX + phaseX) * ampX;
          n.y = baseY + Math.sin(t * freqY + phaseY) * ampY;
          // Slow hue cycle — wraps every ~6s for visual interest.
          const f = n.filter;
          if (f) f.hueRotate = hueBase + t * 0.6;
        },
      }),
    );
  }
  return s;
}

export function sceneL6(w: number, h: number): Scene {
  const s = createScene();
  const colW = (w - 48) / 3;
  const colH = h - 120;
  const yTop = 80;
  // Backdrop rects.
  const backdrops: Array<{ r: number; g: number; b: number }> = [
    { r: 1.0, g: 0.85, b: 0.30 }, // warm yellow
    { r: 0.95, g: 0.30, b: 0.55 }, // pink
    { r: 0.25, g: 0.65, b: 0.95 }, // blue
  ];
  // Per-column overlay colors chosen so multiply produces a clear visual change:
  //   yellow × cyan ≈ mid-tone (each channel multiplied)
  //   pink × green  ≈ darker (low G channel → low result)
  //   blue × red    ≈ darker (low G channel → low result)
  // Screen uses the same colors so it lightens noticeably.
  const overlays: Array<{ r: number; g: number; b: number }> = [
    { r: 0.2, g: 0.7, b: 0.9 }, // cyan
    { r: 0.3, g: 0.9, b: 0.4 }, // green
    { r: 0.9, g: 0.3, b: 0.3 }, // red
  ];
  const circleSize = Math.min(colW, colH) * 0.22;
  const circleRadius = circleSize * 0.5;
  let z = 0;
  for (let c = 0; c < 3; c++) {
    const cx = 16 + c * (colW + 8);
    addNode(s, makeRect({
      x: cx, y: yTop, w: colW, h: colH,
      radius: 12,
      fill: { r: backdrops[c].r, g: backdrops[c].g, b: backdrops[c].b, a: 1 },
      zIndex: z++,
    }));
    // Row 1: multiply overlay — should darken to per-channel product.
    addNode(s, makeRect({
      x: cx + colW * 0.25 - circleRadius * 1.5, y: yTop + colH * 0.20,
      w: circleSize * 1.5, h: circleSize * 1.5,
      radius: circleRadius * 1.5,
      fill: { r: overlays[c].r, g: overlays[c].g, b: overlays[c].b, a: 1 },
      mixBlendMode: "multiply",
      zIndex: z++,
    }));
    // Row 1: screen overlay — should lighten (bg + cs - bg*cs).
    addNode(s, makeRect({
      x: cx + colW * 0.75 - circleRadius * 1.5, y: yTop + colH * 0.20,
      w: circleSize * 1.5, h: circleSize * 1.5,
      radius: circleRadius * 1.5,
      fill: { r: overlays[c].r, g: overlays[c].g, b: overlays[c].b, a: 1 },
      mixBlendMode: "screen",
      zIndex: z++,
    }));
    // Row 2: normal overlay — control (unblended, shows the overlay color).
    addNode(s, makeRect({
      x: cx + colW * 0.5 - circleRadius * 0.5, y: yTop + colH * 0.65,
      w: circleSize * 0.5, h: circleSize * 0.5,
      radius: circleRadius * 0.5,
      fill: { r: overlays[c].r, g: overlays[c].g, b: overlays[c].b, a: 1 },
      mixBlendMode: "normal",
      zIndex: z++,
    }));
    // Labels under each column.
    addNode(s, makeText({
      x: cx, y: yTop + colH + 8, w: colW, h: 20,
      text: `multiply · screen · normal`,
      textColor: { r: 1, g: 1, b: 1, a: 1 },
      fill: { r: 1, g: 1, b: 1, a: 1 },
      fontSize: 12,
      zIndex: z++,
    }));
  }
  return s;
}

export const SCENES: Record<string, (w: number, h: number) => Scene> = {
  A: sceneA,
  B: sceneB,
  C: sceneC,
  D: sceneD,
  E: sceneE,
  F: sceneF,
  G: sceneG,
  L6: sceneL6,
  L7: sceneL7,
  studio: sceneStudio,
};

export function exportSceneIds(s: Scene): NodeId[] {
  return Array.from(s.nodes.keys());
}
